/**
 * RulePreviewDialog - Modal de visualização de regra de roteamento
 *
 * Exibe as ações da regra como uma conversa estilo WhatsApp:
 * bolhas verdes para mensagens enviadas e pílulas centrais para
 * ações operacionais (delays, atribuições, tags). Somente leitura.
 *
 * @version 2.0.0
 * @date 2026-06-03
 *
 * CHANGELOG:
 *   v2.0.0 - Redesign WhatsApp Chat Metaphor (header limpo, fundo com doodles, footer separado).
 *   v1.0.0 - Versão inicial baseada em cards neutros.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Zap } from 'lucide-react';
import { RulePreviewActionCard } from './RulePreviewActionCard';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('RulePreviewDialog');

// =====================================================
// INTERFACES
// =====================================================

interface RulePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ruleId: string;
  ruleName: string;
  ruleDescription?: string | null;
}

interface RuleAction {
  id: string;
  action_type: string;
  action_value: Record<string, unknown>;
  delay_seconds: number | null;
  sort_order: number;
}

// Pattern de doodles do WhatsApp (SVG embutido como data URL, sem dependência externa)
const WHATSAPP_DOODLE_BG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'><g fill='none' stroke='%23000' stroke-opacity='0.04' stroke-width='1.2'><circle cx='10' cy='10' r='4'/><path d='M30 18 l6 -6 l6 6 l-6 6 z'/><path d='M58 8 q6 4 0 12'/><circle cx='70' cy='30' r='3'/><path d='M14 42 q8 -4 14 0'/><path d='M44 48 l5 0 l0 5 l-5 0 z'/><circle cx='66' cy='60' r='5'/><path d='M10 70 l8 0'/><path d='M30 70 q6 -6 12 0'/></g></svg>\")";

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function RulePreviewDialog({
  open,
  onOpenChange,
  ruleId,
  ruleName,
  ruleDescription,
}: RulePreviewDialogProps) {
  // Buscar ações da regra via RPC (filtra client-side)
  const { data: actions, isLoading } = useQuery({
    queryKey: ['rule-preview-actions', ruleId],
    queryFn: async () => {
      logger.debug('Buscando ações da regra para preview via RPC', { ruleId });

      const { data, error } = await supabase.rpc('get_tenant_routing_rules_full');

      if (error) {
        logger.error('Erro ao buscar ações da regra', error);
        throw error;
      }

      const list = (Array.isArray(data) ? data : []) as unknown as Array<{
        id: string;
        actions?: RuleAction[];
      }>;
      const rule = list.find((r) => r.id === ruleId);
      const ruleActions = (rule?.actions || []) as RuleAction[];

      logger.debug('Ações encontradas', { count: ruleActions.length });
      return ruleActions;
    },
    enabled: open && !!ruleId,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header limpo (branco) */}
        <DialogHeader className="px-5 py-4 border-b border-border space-y-1 text-left">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <Zap className="h-5 w-5 text-amber-500 fill-amber-500" />
            Preview: {ruleName}
          </DialogTitle>
          {ruleDescription && (
            <p className="text-xs text-muted-foreground font-mono ml-7 truncate">
              {ruleDescription}
            </p>
          )}
        </DialogHeader>

        {/* Área de chat com fundo estilo WhatsApp */}
        <div
          className="flex-1 min-h-0 bg-[#efeae2] dark:bg-slate-900"
          style={{
            backgroundImage: WHATSAPP_DOODLE_BG,
            backgroundRepeat: 'repeat',
          }}
        >
          <ScrollArea className="h-full max-h-[60vh]">
            <div className="p-4 space-y-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : actions && actions.length > 0 ? (
                actions.map((action, index) => (
                  <RulePreviewActionCard
                    key={action.id}
                    actionType={action.action_type}
                    actionValue={action.action_value}
                    delaySeconds={action.delay_seconds || 0}
                    index={index}
                  />
                ))
              ) : (
                <div className="flex justify-center py-8">
                  <div className="bg-white/85 dark:bg-slate-800/85 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm">
                    <span className="text-[11px] text-slate-700 dark:text-slate-200 font-medium uppercase tracking-wider">
                      Nenhuma ação configurada
                    </span>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border bg-muted/30 flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
